import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { usePermissions } from "../../../common/member/MemberContext";
import { AdminPermissions } from "../../../model/auth/Permissions";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";

const { Text } = Typography;

// DNS-label-shaped: lowercase a-z / 0-9 / hyphen, no leading/trailing hyphen,
// max 63 chars. Mirrors the regex on the API and the DB CHECK constraint so
// the operator sees the same validation everywhere.
const SUBDOMAIN_SLUG_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export interface IdentitySectionValue {
    clubName: string;
    pageTitle: string;
    tagline: string;
    faviconUrl: string;
    logoUrl: string;
    subdomainSlug: string;
}

export const EMPTY_IDENTITY: IdentitySectionValue = {
    clubName: '',
    pageTitle: '',
    tagline: '',
    faviconUrl: '',
    logoUrl: '',
    subdomainSlug: '',
};

interface IdentitySectionProps extends SectionPanelProps<IdentitySectionValue> {
    // Allow the host to force slug visibility independent of the caller's
    // permissions (the wizard needs this so a Fortius admin can set the slug
    // on a brand-new tenant even though the tenant has no members yet).
    showSlugField?: boolean;
}

// Controlled section component for the Identity + Subdomain settings. The
// host owns the value (so the wizard can pass through to its review step,
// and the settings page can wire Save/Discard around the form) — this
// component only handles input rendering and field-level validation.
export const IdentitySection: React.FC<IdentitySectionProps> = ({
    value,
    onChange,
    readOnly,
    showSlugField,
}) => {
    const { permissions } = usePermissions();

    const isFortiusAdmin = useMemo(
        () => Array.isArray(permissions?.permissions)
            && permissions.permissions.some(p => p.name === AdminPermissions.FortiusAdmin),
        [permissions]
    );

    const slugVisible = showSlugField ?? isFortiusAdmin;

    const update = <K extends keyof IdentitySectionValue>(key: K, next: string) => {
        onChange({ ...value, [key]: next });
    };

    const slugInvalid = value.subdomainSlug.length > 0
        && !SUBDOMAIN_SLUG_REGEX.test(value.subdomainSlug);

    return (
        <Form
            layout="vertical"
            data-testid="settings-identity-form"
            disabled={readOnly}
        >
            <Form.Item
                label="Club name"
                required
                validateStatus={value.clubName.trim() === '' ? 'error' : undefined}
                help={value.clubName.trim() === '' ? 'Club name is required.' : undefined}
            >
                <Input
                    data-testid="settings-identity-clubname"
                    value={value.clubName}
                    onChange={(e) => update('clubName', e.target.value)}
                    autoFocus
                />
            </Form.Item>
            <Form.Item label="Page title">
                <Input
                    data-testid="settings-identity-pagetitle"
                    value={value.pageTitle}
                    onChange={(e) => update('pageTitle', e.target.value)}
                />
            </Form.Item>
            <Form.Item label="Tagline">
                <Input
                    data-testid="settings-identity-tagline"
                    value={value.tagline}
                    onChange={(e) => update('tagline', e.target.value)}
                />
            </Form.Item>
            <Form.Item
                label="Logo URL"
                help="Absolute URL or relative path served from the tenant's S3 bucket."
            >
                <Input
                    data-testid="settings-identity-logo-url"
                    value={value.logoUrl}
                    onChange={(e) => update('logoUrl', e.target.value)}
                />
            </Form.Item>
            <Form.Item label="Favicon URL">
                <Input
                    data-testid="settings-identity-favicon-url"
                    value={value.faviconUrl}
                    onChange={(e) => update('faviconUrl', e.target.value)}
                />
            </Form.Item>
            {slugVisible && (
                <Form.Item
                    label={<Space>Subdomain slug <Text type="secondary">(super-admin only)</Text></Space>}
                    validateStatus={slugInvalid ? 'error' : undefined}
                    help={slugInvalid
                        ? 'Lowercase letters, digits, and hyphens; cannot start or end with a hyphen; up to 63 characters.'
                        : 'e.g. "richmond-tc" — used as the tenant\'s public subdomain.'}
                >
                    <Input
                        data-testid="settings-identity-subdomain-slug"
                        value={value.subdomainSlug}
                        onChange={(e) => update('subdomainSlug', e.target.value)}
                    />
                </Form.Item>
            )}
        </Form>
    );
};

// Adapter that mounts the controlled IdentitySection inside the per-client
// Settings shell: loads the current config, tracks dirty state, and exposes
// a Save button that calls the PUT endpoint and refreshes from the server.
// Mounted via SETTINGS_SECTIONS so the shell doesn't need a special-case for
// Identity vs. other sections.
export const IdentitySectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [initial, setInitial] = useState<IdentitySectionValue | null>(null);
    const [value, setValue] = useState<IdentitySectionValue>(EMPTY_IDENTITY);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            // Surface the bad URL rather than rendering an empty form populated
            // with defaults that the operator might then "save" against the
            // wrong (or no) tenant.
            setLoadError('Invalid client id in URL.');
            return;
        }
        // Clear any prior "invalid id" error so navigating from a bad URL to a
        // good one re-renders the form rather than sticking on the alert.
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const config = await service.getConfig(clientId);
                if (cancelled) return;
                if (!config) {
                    setInitial(EMPTY_IDENTITY);
                    setValue(EMPTY_IDENTITY);
                    return;
                }
                const branding = config.settings.branding;
                const loaded: IdentitySectionValue = {
                    clubName: branding.clubName ?? '',
                    pageTitle: branding.pageTitle ?? '',
                    tagline: branding.tagline ?? '',
                    faviconUrl: branding.faviconUrl ?? '',
                    logoUrl: branding.logoUrl ?? '',
                    // The slug rides alongside the branding bag on /config and
                    // is null until a Fortius admin assigns one; treat null as
                    // an empty input so the form is still controllable.
                    subdomainSlug: config.subdomainSlug ?? '',
                };
                setInitial(loaded);
                setValue(loaded);
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load settings.');
            }
        })();
        return () => { cancelled = true; };
    }, [service, clientId]);

    const dirty = initial !== null && JSON.stringify(initial) !== JSON.stringify(value);

    const handleSave = async () => {
        if (!Number.isFinite(clientId)) return;
        setSaving(true);
        setSaveError(null);
        try {
            const trimmedSlug = value.subdomainSlug.trim();
            // Only send the slug when it changed since load. The API
            // distinguishes three states:
            //   - undefined → field absent in payload → no change.
            //   - ""        → explicit clear (operator wiped the field).
            //   - non-empty → validate + set.
            // Sending the trimmed value (including "") when the operator
            // changed the field is what makes "wipe the slug" actually
            // retire the public subdomain instead of silently no-op'ing.
            const slugChanged = trimmedSlug !== (initial?.subdomainSlug ?? '');
            await service.updateIdentity(clientId, {
                clubName: value.clubName.trim(),
                pageTitle: value.pageTitle.trim(),
                tagline: value.tagline.trim(),
                faviconUrl: value.faviconUrl.trim(),
                logoUrl: value.logoUrl.trim(),
                subdomainSlug: slugChanged ? trimmedSlug : undefined,
            });
            // Save baseline as the trimmed-and-saved values so the button
            // re-disables and the slug field stays populated with what the
            // server now has (was previously wiped, which made it look like
            // the save had lost the slug).
            const next: IdentitySectionValue = {
                clubName: value.clubName.trim(),
                pageTitle: value.pageTitle.trim(),
                tagline: value.tagline.trim(),
                faviconUrl: value.faviconUrl.trim(),
                logoUrl: value.logoUrl.trim(),
                subdomainSlug: trimmedSlug,
            };
            setInitial(next);
            setValue(next);
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-identity-load-error" />;
    }
    if (initial === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    const clubNameMissing = value.clubName.trim() === '';

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <IdentitySection value={value} onChange={setValue} />
            {saveError && (
                <Alert type="error" message={saveError} data-testid="settings-identity-save-error" />
            )}
            <Space>
                <Button
                    type="primary"
                    data-testid="settings-identity-save-btn"
                    onClick={handleSave}
                    loading={saving}
                    // Club name is the one truly required field — Save stays
                    // disabled while it's empty so an operator can't silently
                    // wipe the tenant's display name.
                    disabled={!dirty || clubNameMissing}
                >
                    Save changes
                </Button>
                <Button
                    data-testid="settings-identity-discard-btn"
                    onClick={() => initial && setValue(initial)}
                    disabled={!dirty || saving}
                >
                    Discard
                </Button>
            </Space>
        </Space>
    );
};
